import { FileText, UploadCloud } from "lucide-react";
import { type DragEvent, useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { parseArchitecture } from "@/lib/arch-parser";
import { useArchViz } from "./ArchVizContext";

const SAMPLE_PATH = "/samples/sample.architecture.md";

export function DropZone() {
  const { dispatch } = useArchViz();
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback(
    async (file: File) => {
      if (!file.name.endsWith(".md")) {
        toast.error("Please drop a markdown (.md) file.");
        return;
      }
      try {
        const text = await file.text();
        const data = parseArchitecture(text);
        dispatch({ type: "LOAD", architecture: data });
        toast.success(`Loaded ${data.meta.name}`);
      } catch (err) {
        console.error("[archviz] failed to parse file", err);
        toast.error("Could not parse file. Check console for details.");
      }
    },
    [dispatch],
  );

  const handleSample = useCallback(async () => {
    try {
      const res = await fetch(SAMPLE_PATH);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const text = await res.text();
      const data = parseArchitecture(text);
      dispatch({ type: "LOAD", architecture: data });
      toast.success(`Loaded sample: ${data.meta.name}`);
    } catch (err) {
      console.error("[archviz] failed to load sample", err);
      toast.error("Sample file not found at /samples/sample.architecture.md");
    }
  }, [dispatch]);

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) void handleFile(file);
  };

  return (
    <div
      className="flex min-h-screen items-center justify-center p-8"
      onDragOver={(e) => {
        e.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={onDrop}
    >
      <div className="max-w-2xl w-full">
        <div className="text-center mb-8">
          <h1 className="text-4xl mb-3">ArchViz</h1>
          <p className="text-muted-foreground">
            Drop an architecture markdown file to explore it as an interactive C4 diagram.
          </p>
        </div>

        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className={`w-full rounded-xl border-2 border-dashed p-16 transition-all ${
            isDragging
              ? "border-primary bg-primary/5 scale-[1.01]"
              : "border-border bg-muted/30 hover:bg-muted/60"
          }`}
        >
          <div className="flex flex-col items-center gap-4">
            <UploadCloud
              className={`h-12 w-12 ${isDragging ? "text-primary" : "text-muted-foreground"}`}
            />
            <div>
              <div className="text-lg font-medium">
                Drop your <code className="font-mono text-base">architecture.md</code> here
              </div>
              <div className="text-sm text-muted-foreground mt-1">or click to browse</div>
            </div>
          </div>
        </button>

        <input
          ref={inputRef}
          type="file"
          accept=".md,text/markdown"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handleFile(file);
            e.target.value = "";
          }}
        />

        <div className="mt-6 flex items-center justify-center gap-3">
          <span className="text-sm text-muted-foreground">No file handy?</span>
          <Button variant="outline" size="sm" onClick={handleSample}>
            <FileText className="h-4 w-4 mr-2" />
            Load sample
          </Button>
        </div>
      </div>
    </div>
  );
}
